import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock3,
  Compass,
  Gift,
  Heart,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
  Users,
  WandSparkles,
  Zap,
} from "lucide-react";
import { RENDERS } from "../data/renders";

const API_BASE = (
  process.env.REACT_APP_ROMIND_IDENTITY_API_URL ||
  "https://9xu6s9sl3j.execute-api.ap-northeast-2.amazonaws.com"
).replace(/\/$/, "");
const APP_LINK = "romindkids://gift";
const PLAY_URL = "https://play.google.com/store/apps/details?id=tech.romindkids.kids";

const translations = {
  en: {
    language: "Language",
    eyebrow: "A LAUNCH GIFT FOR THE FIRST 100 FAMILIES",
    titleBefore: "Give your child",
    titleAccent: "90 days inside a universe made for them.",
    body: "Not another app to scroll. ROMIND KIDS is a safer creative world where children aged 9–14 can explore interests, create, learn, join clubs and grow — while parents keep clarity and control.",
    primaryCta: "Claim 90 days free",
    installCta: "Install from Google Play",
    secondaryCta: "See what is included",
    noPayment: "No payment",
    noRenewal: "No automatic renewal",
    fivePlans: "Any of 5 plans",
    remaining: "gifts still available",
    reserved: "reserved now",
    claimed: "families already joined",
    live: "LIVE AVAILABILITY",
    loading: "Checking live availability…",
    ended: "All 100 gifts have been claimed.",
    error: "Live availability could not be loaded.",
    retry: "Try again",
    refresh: "Refresh",
    discoverEyebrow: "90 DAYS TO DISCOVER MORE",
    discoverTitle: "A real chance to experience the whole ROMIND KIDS world.",
    discoverBody:
      "Ninety days is enough time to find favorite clubs, complete meaningful challenges, build creative confidence and understand which experience fits your family best.",
    benefits: [
      [
        "A safer social world",
        "Age-aware profiles, protected communication and RONI guidance help children connect with more confidence.",
      ],
      [
        "Academy that sparks curiosity",
        "Learning paths in coding, AI, art, games and smart devices turn interest into visible progress.",
      ],
      [
        "Clubs and activities",
        "Children find communities around what they genuinely love — not whatever an endless feed pushes next.",
      ],
      [
        "Challenges and rewards",
        "Missions, achievements and rewards make participation purposeful, motivating and fun.",
      ],
      [
        "Create, share and express",
        "Profiles, Stories and creative tools give children space to build an identity around interests and ideas.",
      ],
      [
        "Confidence for parents",
        "Parent Controls, separate child progress and family-aware settings bring clarity without turning care into surveillance.",
      ],
    ],
    childTitle: "For children: a world that feels like theirs.",
    childBody:
      "Creativity, friendship, discovery and progress — in an experience built specifically for ages 9–14, not a smaller copy of an adult network.",
    parentTitle: "For parents: freedom with a safety layer.",
    parentBody:
      "Thoughtful controls, safer communication and transparent family settings help parents stay informed while children keep room to grow.",
    allAccess:
      "Choose any of five plans — including experiences with closed challenges, special events, early access and expanded family possibilities where the selected plan includes them.",
    urgencyEyebrow: "ONLY 100 ACTIVATIONS",
    urgencyTitle:
      "The counter is real. When the gifts are gone, the campaign is over.",
    urgencyBody:
      "Opening this page does not take a place. A gift is reserved for 30 minutes only after a verified parent signs in, giving you time to choose the right plan.",
    urgencyCta: "Open ROMIND KIDS and claim yours",
    rulesTitle: "Simple, transparent and without traps.",
    reserveTitle: "30-minute fair reservation",
    reserveBody:
      "Your place is held only after verified parent access. Unfinished reservations return automatically.",
    familyTitle: "One gift per verified family",
    familyBody:
      "Up to three child profiles under the same parent can share the family experience without consuming extra gifts.",
    planTitle: "90 days on your chosen plan",
    planBody:
      "Choose one of five plans. It runs for 90 days without payment or automatic renewal.",
    note: "If ROMIND KIDS is not installed yet, get it from Google Play, then return to this page and tap Claim 90 days free. The gift is activated by a verified parent inside the app.",
  },
  ru: {
    language: "Язык",
    eyebrow: "ПОДАРОК НА СТАРТЕ ДЛЯ ПЕРВЫХ 100 СЕМЕЙ",
    titleBefore: "Подарите ребёнку",
    titleAccent: "90 дней во вселенной, созданной для него.",
    body: "Не ещё одно приложение, которое нужно бесконечно листать. ROMIND KIDS — более безопасный творческий мир, где дети 9–14 лет исследуют интересы, создают, учатся, вступают в клубы и растут, а родители сохраняют ясность и контроль.",
    primaryCta: "Забрать 90 дней бесплатно",
    installCta: "Установить из Google Play",
    secondaryCta: "Посмотреть все возможности",
    noPayment: "Без оплаты",
    noRenewal: "Без автопродления",
    fivePlans: "Любой из 5 тарифов",
    remaining: "подарочных мест осталось",
    reserved: "сейчас забронировано",
    claimed: "семей уже получили подарок",
    live: "МЕСТА В РЕАЛЬНОМ ВРЕМЕНИ",
    loading: "Проверяем наличие подарков…",
    ended: "Все 100 подарков уже активированы.",
    error: "Не удалось загрузить актуальное количество мест.",
    retry: "Повторить",
    refresh: "Обновить",
    discoverEyebrow: "90 ДНЕЙ, ЧТОБЫ ОТКРЫТЬ БОЛЬШЕ",
    discoverTitle: "Настоящая возможность попробовать весь мир ROMIND KIDS.",
    discoverBody:
      "За 90 дней ребёнок успеет найти любимые клубы, пройти полезные испытания, раскрыть творческие способности, а семья — понять, какой формат подходит именно ей.",
    benefits: [
      [
        "Более безопасный социальный мир",
        "Возрастные профили, защищённое общение и поддержка RONI помогают детям увереннее знакомиться и дружить.",
      ],
      [
        "Academy, которая зажигает интерес",
        "Направления по программированию, ИИ, цифровому искусству, играм и умным устройствам превращают любопытство в заметный прогресс.",
      ],
      [
        "Клубы и Activities",
        "Ребёнок находит сообщество вокруг того, что любит сам, а не вокруг того, что бесконечно подсовывает обычная лента.",
      ],
      [
        "Challenges и награды",
        "Задания, достижения и Rewards делают участие осмысленным, мотивирующим и по-настоящему увлекательным.",
      ],
      [
        "Создавать, делиться, проявляться",
        "Профиль, Stories и творческие инструменты дают пространство собирать свою личность вокруг интересов и идей.",
      ],
      [
        "Спокойствие для родителей",
        "Parent Controls, отдельный прогресс детей и семейные настройки дают ясность, не превращая заботу в тотальную слежку.",
      ],
    ],
    childTitle: "Для ребёнка — мир, который действительно его понимает.",
    childBody:
      "Творчество, дружба, открытия и рост в среде, созданной специально для 9–14 лет, а не в уменьшенной копии взрослой соцсети.",
    parentTitle: "Для родителей — свобода ребёнку, спокойствие семье.",
    parentBody:
      "Продуманные настройки, более безопасное общение и прозрачное управление помогают быть в курсе, оставляя ребёнку пространство для развития.",
    allAccess:
      "Можно выбрать любой из пяти тарифов — в том числе варианты с закрытыми Challenges, специальными событиями, ранним доступом и расширенными семейными возможностями там, где они входят в выбранный тариф.",
    urgencyEyebrow: "ТОЛЬКО 100 АКТИВАЦИЙ",
    urgencyTitle:
      "Счётчик настоящий. Когда подарки закончатся — акция завершится.",
    urgencyBody:
      "Просмотр страницы не занимает место. Подарок бронируется на 30 минут только после подтверждённого входа родителя — этого достаточно, чтобы спокойно выбрать тариф.",
    urgencyCta: "Открыть ROMIND KIDS и забрать подарок",
    rulesTitle: "Просто, прозрачно и без ловушек.",
    reserveTitle: "Честная бронь на 30 минут",
    reserveBody:
      "Место удерживается только после подтверждённого входа родителя. Незавершённая бронь освобождается автоматически.",
    familyTitle: "Один подарок на подтверждённую семью",
    familyBody:
      "До трёх детских профилей внутри одной семьи могут пользоваться семейным форматом и не расходуют дополнительные подарки.",
    planTitle: "90 дней выбранного тарифа",
    planBody:
      "Выберите один из пяти тарифов. Он действует 90 дней без оплаты и автоматического продления.",
    note: "Если ROMIND KIDS ещё не установлен, скачайте его из Google Play, затем вернитесь сюда и нажмите «Забрать 90 дней бесплатно». Подарок активирует подтверждённый родитель внутри приложения.",
  },
  ko: {
    language: "언어",
    eyebrow: "첫 100가족을 위한 출시 기념 선물",
    titleBefore: "아이에게 선물하세요 —",
    titleAccent: "아이를 위해 만든 세계에서의 90일.",
    body: "끝없이 넘기는 또 하나의 앱이 아닙니다. ROMIND KIDS는 9–14세 어린이가 관심사를 찾고, 만들고, 배우고, 클럽에 참여하며 성장하는 더 안전한 창의적 세계입니다. 부모는 명확한 정보와 통제권을 유지합니다.",
    primaryCta: "90일 무료 선물 받기",
    installCta: "Google Play에서 설치",
    secondaryCta: "포함된 경험 보기",
    noPayment: "결제 없음",
    noRenewal: "자동 갱신 없음",
    fivePlans: "5개 요금제 중 선택",
    remaining: "남은 선물",
    reserved: "현재 예약",
    claimed: "참여한 가족",
    live: "실시간 남은 수",
    loading: "남은 선물을 확인하는 중…",
    ended: "100개의 선물이 모두 활성화되었습니다.",
    error: "현재 남은 수를 불러올 수 없습니다.",
    retry: "다시 시도",
    refresh: "새로고침",
    discoverEyebrow: "더 많은 것을 발견하는 90일",
    discoverTitle: "ROMIND KIDS의 전체 세계를 경험할 진짜 기회.",
    discoverBody:
      "90일 동안 아이는 좋아하는 클럽을 찾고, 의미 있는 도전을 완수하고, 창의적 자신감을 키울 수 있습니다. 가족은 자신에게 맞는 경험을 충분히 확인할 수 있습니다.",
    benefits: [
      [
        "더 안전한 소셜 세계",
        "연령별 프로필, 보호된 소통, RONI 안내가 더 자신 있는 연결을 돕습니다.",
      ],
      [
        "호기심을 키우는 Academy",
        "코딩, AI, 아트, 게임, 스마트 기기 학습이 관심을 실제 성장으로 바꿉니다.",
      ],
      [
        "클럽과 Activities",
        "끝없는 피드가 아니라 아이가 정말 좋아하는 관심사를 중심으로 친구와 커뮤니티를 찾습니다.",
      ],
      [
        "Challenges와 보상",
        "미션, 성취, Rewards가 참여를 의미 있고 재미있게 만듭니다.",
      ],
      [
        "만들고 공유하고 표현하기",
        "프로필, Stories, 창작 도구로 관심사와 아이디어를 자신만의 방식으로 표현합니다.",
      ],
      [
        "부모를 위한 안심",
        "Parent Controls, 자녀별 진행 상황, 가족 설정이 감시가 아닌 명확함을 제공합니다.",
      ],
    ],
    childTitle: "아이에게는 자신을 이해하는 세계.",
    childBody:
      "성인 SNS의 축소판이 아니라 9–14세를 위해 설계된 창의성, 우정, 발견, 성장의 공간입니다.",
    parentTitle: "부모에게는 안전 장치가 있는 자유.",
    parentBody:
      "세심한 설정과 더 안전한 소통, 투명한 가족 관리가 아이의 성장 공간을 지키면서 부모의 이해를 돕습니다.",
    allAccess:
      "다섯 가지 요금제 중 하나를 선택할 수 있으며, 선택한 요금제에 따라 비공개 Challenges, 특별 이벤트, 얼리 액세스 및 확장된 가족 기능을 경험할 수 있습니다.",
    urgencyEyebrow: "단 100번의 활성화",
    urgencyTitle:
      "카운터는 실제입니다. 선물이 모두 소진되면 캠페인이 종료됩니다.",
    urgencyBody:
      "이 페이지를 보는 것만으로 자리를 차지하지 않습니다. 인증된 부모가 로그인한 후 요금제를 고르는 30분 동안만 예약됩니다.",
    urgencyCta: "ROMIND KIDS를 열고 선물 받기",
    rulesTitle: "간단하고 투명하며 숨은 함정이 없습니다.",
    reserveTitle: "공정한 30분 예약",
    reserveBody:
      "인증된 부모가 접속한 뒤에만 예약되며, 완료되지 않은 예약은 자동으로 반환됩니다.",
    familyTitle: "인증된 가족당 한 번",
    familyBody:
      "같은 부모 계정 아래 최대 3명의 자녀 프로필이 가족 경험을 공유하며 추가 선물을 사용하지 않습니다.",
    planTitle: "선택한 요금제로 90일",
    planBody:
      "다섯 요금제 중 하나를 골라 결제와 자동 갱신 없이 90일 이용합니다.",
    note: "ROMIND KIDS가 설치되지 않았다면 Google Play에서 설치한 뒤 이 페이지로 돌아와 90일 무료 선물 받기를 누르세요. 인증된 부모가 앱에서 선물을 활성화합니다.",
  },
  es: {
    language: "Idioma",
    eyebrow: "UN REGALO DE LANZAMIENTO PARA LAS PRIMERAS 100 FAMILIAS",
    titleBefore: "Regala a tu hijo",
    titleAccent: "90 días en un universo creado para él.",
    body: "No es otra aplicación para deslizar sin fin. ROMIND KIDS es un mundo creativo más seguro donde niños de 9 a 14 años exploran intereses, crean, aprenden, se unen a clubes y crecen, mientras sus padres mantienen claridad y control.",
    primaryCta: "Conseguir 90 días gratis",
    installCta: "Instalar desde Google Play",
    secondaryCta: "Ver todo lo incluido",
    noPayment: "Sin pago",
    noRenewal: "Sin renovación automática",
    fivePlans: "Cualquiera de 5 planes",
    remaining: "regalos disponibles",
    reserved: "reservados ahora",
    claimed: "familias ya unidas",
    live: "DISPONIBILIDAD EN VIVO",
    loading: "Comprobando los regalos disponibles…",
    ended: "Los 100 regalos ya se han activado.",
    error: "No se pudo cargar la disponibilidad actual.",
    retry: "Intentar de nuevo",
    refresh: "Actualizar",
    discoverEyebrow: "90 DÍAS PARA DESCUBRIR MÁS",
    discoverTitle: "Una oportunidad real de vivir todo el mundo ROMIND KIDS.",
    discoverBody:
      "En 90 días, un niño puede encontrar sus clubes favoritos, completar retos con sentido, desarrollar confianza creativa y ayudar a la familia a descubrir qué experiencia le encaja mejor.",
    benefits: [
      [
        "Un mundo social más seguro",
        "Perfiles por edad, comunicación protegida y la guía de RONI ayudan a conectar con más confianza.",
      ],
      [
        "Academy que despierta curiosidad",
        "Rutas de programación, IA, arte, juegos y dispositivos inteligentes convierten el interés en progreso.",
      ],
      [
        "Clubes y Activities",
        "Los niños encuentran comunidades sobre lo que de verdad les gusta, no sobre lo que impone un feed infinito.",
      ],
      [
        "Challenges y recompensas",
        "Misiones, logros y Rewards hacen que participar tenga propósito, motivación y diversión.",
      ],
      [
        "Crear, compartir y expresarse",
        "Perfiles, Stories y herramientas creativas ofrecen espacio para construir identidad con intereses e ideas.",
      ],
      [
        "Tranquilidad para los padres",
        "Parent Controls, progreso separado y ajustes familiares aportan claridad sin convertir el cuidado en vigilancia.",
      ],
    ],
    childTitle: "Para los niños: un mundo que se siente suyo.",
    childBody:
      "Creatividad, amistad, descubrimiento y progreso en una experiencia diseñada para 9–14 años, no una versión pequeña de una red adulta.",
    parentTitle: "Para los padres: libertad con una capa de seguridad.",
    parentBody:
      "Controles cuidados, comunicación más segura y ajustes familiares transparentes permiten estar informados y dar espacio para crecer.",
    allAccess:
      "Elige cualquiera de cinco planes, incluidas experiencias con Challenges cerrados, eventos especiales, acceso anticipado y opciones familiares ampliadas cuando formen parte del plan elegido.",
    urgencyEyebrow: "SOLO 100 ACTIVACIONES",
    urgencyTitle: "El contador es real. Cuando se acaben, la campaña termina.",
    urgencyBody:
      "Visitar esta página no ocupa plaza. El regalo se reserva durante 30 minutos solo después de que un padre verificado inicie sesión.",
    urgencyCta: "Abrir ROMIND KIDS y conseguir el regalo",
    rulesTitle: "Simple, transparente y sin trampas.",
    reserveTitle: "Reserva justa de 30 minutos",
    reserveBody:
      "La plaza se guarda solo tras el acceso parental verificado. Las reservas incompletas vuelven automáticamente.",
    familyTitle: "Un regalo por familia verificada",
    familyBody:
      "Hasta tres perfiles infantiles bajo el mismo padre pueden compartir la experiencia familiar sin gastar regalos extra.",
    planTitle: "90 días con el plan elegido",
    planBody:
      "Elige uno de cinco planes. Dura 90 días sin pago ni renovación automática.",
    note: "Si aún no tienes ROMIND KIDS, instálala desde Google Play, vuelve a esta página y pulsa Conseguir 90 días gratis. El regalo se activa en la aplicación por un padre verificado.",
  },
  fr: {
    language: "Langue",
    eyebrow: "UN CADEAU DE LANCEMENT POUR LES 100 PREMIÈRES FAMILLES",
    titleBefore: "Offrez à votre enfant",
    titleAccent: "90 jours dans un univers pensé pour lui.",
    body: "Pas une application de plus à faire défiler. ROMIND KIDS est un monde créatif plus sûr où les 9–14 ans explorent leurs passions, créent, apprennent, rejoignent des clubs et progressent, tandis que les parents gardent de la visibilité et du contrôle.",
    primaryCta: "Recevoir 90 jours gratuits",
    installCta: "Installer sur Google Play",
    secondaryCta: "Voir tout ce qui est inclus",
    noPayment: "Sans paiement",
    noRenewal: "Sans renouvellement automatique",
    fivePlans: "L’un des 5 forfaits",
    remaining: "cadeaux encore disponibles",
    reserved: "réservés actuellement",
    claimed: "familles déjà inscrites",
    live: "DISPONIBILITÉ EN DIRECT",
    loading: "Vérification des cadeaux disponibles…",
    ended: "Les 100 cadeaux ont déjà été activés.",
    error: "Impossible de charger la disponibilité actuelle.",
    retry: "Réessayer",
    refresh: "Actualiser",
    discoverEyebrow: "90 JOURS POUR DÉCOUVRIR PLUS",
    discoverTitle: "Une vraie chance de vivre tout l’univers ROMIND KIDS.",
    discoverBody:
      "En 90 jours, un enfant peut trouver ses clubs préférés, relever des défis utiles, développer sa confiance créative et permettre à sa famille de choisir l’expérience qui lui correspond.",
    benefits: [
      [
        "Un monde social plus sûr",
        "Profils adaptés à l’âge, communication protégée et accompagnement de RONI favorisent des liens plus sereins.",
      ],
      [
        "Une Academy qui éveille la curiosité",
        "Des parcours en code, IA, art, jeux et objets intelligents transforment l’intérêt en progrès visible.",
      ],
      [
        "Clubs et Activities",
        "Les enfants trouvent des communautés autour de ce qu’ils aiment vraiment, pas de ce qu’impose un fil infini.",
      ],
      [
        "Challenges et récompenses",
        "Missions, réussites et Rewards rendent la participation utile, motivante et amusante.",
      ],
      [
        "Créer, partager, s’exprimer",
        "Profils, Stories et outils créatifs donnent une place aux intérêts, aux idées et à l’identité.",
      ],
      [
        "La sérénité des parents",
        "Parent Controls, progrès séparés et réglages familiaux donnent de la clarté sans transformer l’attention en surveillance.",
      ],
    ],
    childTitle: "Pour les enfants : un monde qui leur ressemble.",
    childBody:
      "Créativité, amitié, découverte et progression dans une expérience conçue pour les 9–14 ans, pas une copie réduite d’un réseau adulte.",
    parentTitle:
      "Pour les parents : de la liberté avec une couche de sécurité.",
    parentBody:
      "Des contrôles réfléchis, des échanges plus sûrs et des réglages transparents permettent de rester informé tout en laissant grandir.",
    allAccess:
      "Choisissez l’un des cinq forfaits, avec selon le forfait des Challenges privés, des événements spéciaux, un accès anticipé et des possibilités familiales étendues.",
    urgencyEyebrow: "SEULEMENT 100 ACTIVATIONS",
    urgencyTitle:
      "Le compteur est réel. Une fois les cadeaux partis, la campagne se termine.",
    urgencyBody:
      "Consulter cette page ne réserve aucune place. Le cadeau est réservé 30 minutes seulement après la connexion vérifiée du parent.",
    urgencyCta: "Ouvrir ROMIND KIDS et recevoir le cadeau",
    rulesTitle: "Simple, transparent et sans piège.",
    reserveTitle: "Réservation équitable de 30 minutes",
    reserveBody:
      "La place est gardée après l’accès vérifié du parent. Une réservation inachevée est automatiquement libérée.",
    familyTitle: "Un cadeau par famille vérifiée",
    familyBody:
      "Jusqu’à trois profils enfant sous le même parent peuvent partager l’expérience familiale sans utiliser de cadeaux supplémentaires.",
    planTitle: "90 jours avec le forfait choisi",
    planBody:
      "Choisissez l’un des cinq forfaits. Il reste actif 90 jours sans paiement ni renouvellement automatique.",
    note: "Si ROMIND KIDS n’est pas installé, téléchargez-le sur Google Play, revenez ici et appuyez sur Recevoir 90 jours gratuits. Un parent vérifié active le cadeau dans l’application.",
  },
  ar: {
    language: "اللغة",
    eyebrow: "هدية إطلاق لأول 100 عائلة",
    titleBefore: "امنح طفلك",
    titleAccent: "90 يومًا في عالم صُمم من أجله.",
    body: "ليس تطبيقًا آخر للتمرير بلا نهاية. ROMIND KIDS عالم إبداعي أكثر أمانًا للأطفال من 9 إلى 14 عامًا لاكتشاف اهتماماتهم والإبداع والتعلم والانضمام إلى النوادي والنمو، مع وضوح وتحكم للوالدين.",
    primaryCta: "احصل على 90 يومًا مجانًا",
    installCta: "التثبيت من Google Play",
    secondaryCta: "شاهد كل ما يتضمنه",
    noPayment: "من دون دفع",
    noRenewal: "من دون تجديد تلقائي",
    fivePlans: "أي من 5 خطط",
    remaining: "هدايا لا تزال متاحة",
    reserved: "محجوزة الآن",
    claimed: "عائلات انضمت بالفعل",
    live: "التوفر المباشر",
    loading: "جارٍ التحقق من الهدايا المتاحة…",
    ended: "تم تفعيل جميع الهدايا المئة.",
    error: "تعذر تحميل العدد المتاح حاليًا.",
    retry: "إعادة المحاولة",
    refresh: "تحديث",
    discoverEyebrow: "90 يومًا لاكتشاف المزيد",
    discoverTitle: "فرصة حقيقية لتجربة عالم ROMIND KIDS بالكامل.",
    discoverBody:
      "خلال 90 يومًا يستطيع الطفل العثور على نواديه المفضلة وإكمال تحديات مفيدة وبناء الثقة الإبداعية، وتستطيع العائلة معرفة التجربة الأنسب لها.",
    benefits: [
      [
        "عالم اجتماعي أكثر أمانًا",
        "ملفات تناسب العمر وتواصل محمي وإرشاد RONI تساعد الأطفال على التواصل بثقة أكبر.",
      ],
      [
        "Academy تشعل الفضول",
        "مسارات البرمجة والذكاء الاصطناعي والفن والألعاب والأجهزة الذكية تحول الاهتمام إلى تقدم واضح.",
      ],
      [
        "النوادي وActivities",
        "يجد الأطفال مجتمعًا حول ما يحبونه فعلًا، لا حول ما تفرضه خلاصة لا تنتهي.",
      ],
      [
        "Challenges والمكافآت",
        "المهمات والإنجازات وRewards تجعل المشاركة هادفة ومحفزة وممتعة.",
      ],
      [
        "الإبداع والمشاركة والتعبير",
        "الملفات وStories وأدوات الإبداع تمنح مساحة لبناء الهوية حول الاهتمامات والأفكار.",
      ],
      [
        "طمأنينة للوالدين",
        "Parent Controls وتقدم كل طفل وإعدادات الأسرة تمنح الوضوح من دون تحويل الرعاية إلى مراقبة.",
      ],
    ],
    childTitle: "للأطفال: عالم يشعرون أنه لهم.",
    childBody:
      "إبداع وصداقة واكتشاف وتقدم في تجربة صُممت لعمر 9–14، وليست نسخة مصغرة من شبكة للكبار.",
    parentTitle: "للوالدين: حرية مع طبقة أمان.",
    parentBody:
      "إعدادات مدروسة وتواصل أكثر أمانًا وإدارة أسرية واضحة تساعد الوالدين على البقاء على اطلاع مع ترك مساحة للنمو.",
    allAccess:
      "اختر أيًا من الخطط الخمس، بما في ذلك تجارب Challenges المغلقة والفعاليات الخاصة والوصول المبكر وإمكانات الأسرة الموسعة عندما تتضمنها الخطة المختارة.",
    urgencyEyebrow: "100 عملية تفعيل فقط",
    urgencyTitle: "العداد حقيقي. عند نفاد الهدايا تنتهي الحملة.",
    urgencyBody:
      "فتح هذه الصفحة لا يحجز مكانًا. تُحجز الهدية 30 دقيقة فقط بعد تسجيل دخول ولي أمر موثّق لاختيار الخطة.",
    urgencyCta: "افتح ROMIND KIDS واحصل على هديتك",
    rulesTitle: "بسيطة وشفافة ومن دون فخاخ.",
    reserveTitle: "حجز عادل لمدة 30 دقيقة",
    reserveBody:
      "يُحجز المكان فقط بعد دخول ولي أمر موثّق، وتُعاد الحجوزات غير المكتملة تلقائيًا.",
    familyTitle: "هدية واحدة لكل عائلة موثّقة",
    familyBody:
      "يمكن لما يصل إلى ثلاثة ملفات أطفال تحت الوالد نفسه مشاركة تجربة الأسرة دون استهلاك هدايا إضافية.",
    planTitle: "90 يومًا على الخطة المختارة",
    planBody: "اختر واحدة من خمس خطط لمدة 90 يومًا من دون دفع أو تجديد تلقائي.",
    note: "إذا لم يكن ROMIND KIDS مثبتًا، نزّله من Google Play، ثم ارجع إلى هذه الصفحة واضغط على احصل على 90 يومًا مجانًا. يفعّل أحد الوالدين الموثّقين الهدية داخل التطبيق.",
  },
};

const benefitIcons = [
  ShieldCheck,
  BookOpen,
  Compass,
  Trophy,
  WandSparkles,
  Heart,
];
const benefitColors = [
  "from-cyan-500/25 to-blue-600/5 border-cyan-400/20 text-cyan-300",
  "from-violet-500/25 to-purple-600/5 border-violet-400/20 text-violet-300",
  "from-fuchsia-500/25 to-pink-600/5 border-fuchsia-400/20 text-fuchsia-300",
  "from-amber-500/25 to-orange-600/5 border-amber-400/20 text-amber-300",
  "from-purple-500/25 to-indigo-600/5 border-purple-400/20 text-purple-300",
  "from-emerald-500/25 to-teal-600/5 border-emerald-400/20 text-emerald-300",
];

const getLanguage = () => {
  const language = String(navigator.language || "en")
    .toLowerCase()
    .split("-")[0];
  return translations[language] ? language : "en";
};

const GiftPage = () => {
  const [language, setLanguage] = useState(getLanguage);
  const [availability, setAvailability] = useState(null);
  const [status, setStatus] = useState("loading");
  const copy = translations[language];
  const isRtl = language === "ar";

  const loadAvailability = useCallback(async (showLoader = false) => {
    if (showLoader) setStatus("loading");
    try {
      const response = await fetch(API_BASE + "/access/gift/availability", {
        headers: { Accept: "application/json" },
      });
      const payload = await response.json();
      if (!response.ok || !payload?.ok || !payload?.data)
        throw new Error("Invalid availability response");
      setAvailability(payload.data);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    document.title = copy.eyebrow + " | ROMIND KIDS";
  }, [copy.eyebrow]);

  useEffect(() => {
    loadAvailability(true);
    const refreshTimer = window.setInterval(
      () => loadAvailability(false),
      30000,
    );
    return () => window.clearInterval(refreshTimer);
  }, [loadAvailability]);

  const rules = useMemo(
    () => [
      {
        icon: Clock3,
        title: copy.reserveTitle,
        body: copy.reserveBody,
        accent: "text-cyan-300 bg-cyan-500/10 border-cyan-400/20",
      },
      {
        icon: Users,
        title: copy.familyTitle,
        body: copy.familyBody,
        accent: "text-purple-300 bg-purple-500/10 border-purple-400/20",
      },
      {
        icon: CheckCircle2,
        title: copy.planTitle,
        body: copy.planBody,
        accent: "text-emerald-300 bg-emerald-500/10 border-emerald-400/20",
      },
    ],
    [copy],
  );

  const available = status === "ready" && availability?.available;

  return (
    <main
      dir={isRtl ? "rtl" : "ltr"}
      className="relative min-h-screen overflow-hidden bg-[#05050A] pt-20 text-white"
    >
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-[-8rem] top-16 h-[34rem] w-[34rem] rounded-full bg-purple-700/20 blur-[150px]" />
        <div className="absolute right-[-8rem] top-[28rem] h-[32rem] w-[32rem] rounded-full bg-cyan-500/15 blur-[150px]" />
        <div className="absolute bottom-0 left-1/3 h-[28rem] w-[28rem] rounded-full bg-fuchsia-600/10 blur-[150px]" />
      </div>

      <section className="relative mx-auto max-w-7xl px-4 pb-20 pt-5 sm:px-6 sm:pt-8 lg:px-8 lg:pb-28">
        <div className="mb-6 flex justify-end">
          <label className="sr-only" htmlFor="gift-language">
            {copy.language}
          </label>
          <select
            id="gift-language"
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            className="rounded-full border border-white/10 bg-[#111020]/90 px-4 py-2 text-sm text-white outline-none backdrop-blur-xl focus:border-cyan-400/50"
          >
            <option value="en">English</option>
            <option value="ru">Русский</option>
            <option value="ko">한국어</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
            <option value="ar">العربية</option>
          </select>
        </div>

        <div className="grid items-center gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:gap-8">
          <div className="relative z-10">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-fuchsia-300/30 bg-fuchsia-500/10 px-4 py-2 text-[11px] font-extrabold tracking-[0.16em] text-fuchsia-200 shadow-[0_0_40px_rgba(217,70,239,0.12)]">
              <Gift size={15} /> {copy.eyebrow}
            </div>
            <h1 className="font-outfit text-5xl font-black leading-[0.98] tracking-[-0.04em] sm:text-6xl lg:text-[4.75rem]">
              <span className="text-white">{copy.titleBefore} </span>
              <span className="gradient-text">{copy.titleAccent}</span>
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-8 text-white/65 sm:text-lg">
              {copy.body}
            </p>

            <div className="mt-7 flex flex-wrap gap-2.5 text-xs font-semibold text-white/75">
              {[copy.noPayment, copy.noRenewal, copy.fivePlans].map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.045] px-4 py-2.5"
                >
                  <CheckCircle2 size={14} className="text-emerald-300" />
                  {item}
                </span>
              ))}
            </div>

            <div className="mt-9 flex flex-col flex-wrap gap-3 sm:flex-row">
              {available ? (
                <a
                  href={APP_LINK}
                  className="group inline-flex min-h-14 items-center justify-center gap-2 rounded-full border border-fuchsia-300/55 bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-500 px-8 text-sm font-extrabold text-white shadow-[0_14px_45px_rgba(168,85,247,0.32)] transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  {copy.primaryCta}{" "}
                  <ArrowRight
                    size={18}
                    className={
                      (isRtl ? "rotate-180 " : "") +
                      "transition-transform group-hover:translate-x-1"
                    }
                  />
                </a>
              ) : null}
              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-full border border-white/25 bg-white px-6 text-sm font-extrabold text-[#140b25] transition hover:bg-fuchsia-50"
              >
                {copy.installCta} <ArrowRight size={17} />
              </a>
              <a
                href="#inside"
                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-8 text-sm font-bold text-white/80 transition hover:border-cyan-400/40 hover:bg-cyan-500/10 hover:text-white"
              >
                {copy.secondaryCta}{" "}
                <Sparkles size={16} className="text-cyan-300" />
              </a>
            </div>
          </div>

          <div className="relative mx-auto min-h-[500px] w-full max-w-[560px] sm:min-h-[590px]">
            <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-purple-500/30 via-fuchsia-500/20 to-cyan-400/25 blur-[70px] animate-glow-pulse" />
            <img
              src={RENDERS.profile}
              alt="ROMIND KIDS profile"
              className="absolute left-[4%] top-[5%] z-20 w-[55%] -rotate-[5deg] drop-shadow-[0_30px_60px_rgba(0,0,0,0.65)] sm:w-[58%]"
            />
            <img
              src={RENDERS.storyPortal}
              alt="ROMIND KIDS Story Portal"
              className="absolute right-[1%] top-[17%] z-10 w-[48%] rotate-[7deg] opacity-95 drop-shadow-[0_30px_60px_rgba(0,0,0,0.65)] sm:w-[50%]"
            />

            <div className="absolute bottom-[2%] left-1/2 z-30 w-[92%] -translate-x-1/2 overflow-hidden rounded-[2rem] border border-cyan-300/25 bg-[#0b0a18]/90 p-5 shadow-[0_25px_70px_rgba(0,0,0,0.65),0_0_55px_rgba(34,211,238,0.10)] backdrop-blur-2xl sm:p-6">
              <div className="flex items-center justify-between gap-4">
                <div className="inline-flex items-center gap-2 text-[10px] font-extrabold tracking-[0.18em] text-emerald-300">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-300" />
                  {copy.live}
                </div>
                <button
                  onClick={() => loadAvailability(true)}
                  aria-label={copy.refresh}
                  className="rounded-full border border-white/10 bg-white/5 p-2 text-white/50 transition hover:text-white"
                >
                  <RefreshCw
                    size={14}
                    className={status === "loading" ? "animate-spin" : ""}
                  />
                </button>
              </div>
              {status === "loading" ? (
                <p className="py-10 text-center text-sm text-white/50">
                  {copy.loading}
                </p>
              ) : null}
              {status === "error" ? (
                <div className="py-7 text-center">
                  <p className="text-sm text-rose-200">{copy.error}</p>
                  <button
                    onClick={() => loadAvailability(true)}
                    className="mt-4 rounded-full border border-rose-300/25 bg-rose-500/10 px-5 py-2 text-sm font-semibold text-rose-100"
                  >
                    {copy.retry}
                  </button>
                </div>
              ) : null}
              {status === "ready" && availability ? (
                <div>
                  <div className="mt-4 flex items-end justify-between gap-4 border-b border-white/8 pb-5">
                    <div>
                      <p className="font-outfit text-6xl font-black leading-none text-white sm:text-7xl">
                        {availability.remaining}
                      </p>
                      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.1em] text-cyan-200">
                        {availability.remaining > 0
                          ? copy.remaining
                          : copy.ended}
                      </p>
                    </div>
                    <div className="flex gap-2 text-center">
                      <div className="rounded-2xl bg-white/[0.045] px-3 py-2">
                        <p className="font-outfit text-xl font-bold">
                          {availability.reserved}
                        </p>
                        <p className="text-[9px] text-white/40">
                          {copy.reserved}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-white/[0.045] px-3 py-2">
                        <p className="font-outfit text-xl font-bold">
                          {availability.used}
                        </p>
                        <p className="text-[9px] text-white/40">
                          {copy.claimed}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-purple-500 to-fuchsia-500 transition-all duration-700"
                      style={{
                        width:
                          Math.max(2, Math.min(100, availability.remaining)) +
                          "%",
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section
        id="inside"
        className="relative border-y border-white/8 bg-white/[0.018] py-24"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-purple-300">
              {copy.discoverEyebrow}
            </p>
            <h2 className="mt-4 font-outfit text-4xl font-black tracking-tight text-white sm:text-5xl">
              {copy.discoverTitle}
            </h2>
            <p className="mt-5 text-base leading-8 text-white/55">
              {copy.discoverBody}
            </p>
          </div>
          <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {copy.benefits.map(([title, body], index) => {
              const Icon = benefitIcons[index];
              return (
                <article
                  key={title}
                  className="group relative overflow-hidden rounded-3xl border border-white/8 bg-[#0b0a16]/80 p-6 transition duration-300 hover:-translate-y-1 hover:border-purple-400/25 hover:shadow-[0_18px_55px_rgba(88,28,135,0.18)]"
                >
                  <div
                    className={
                      "absolute inset-0 bg-gradient-to-br opacity-40 " +
                      benefitColors[index]
                    }
                  />
                  <div className="relative">
                    <div
                      className={
                        "mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border bg-gradient-to-br " +
                        benefitColors[index]
                      }
                    >
                      <Icon size={21} />
                    </div>
                    <h3 className="font-outfit text-xl font-bold text-white">
                      {title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-white/52">
                      {body}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <article className="relative overflow-hidden rounded-[2rem] border border-fuchsia-400/20 bg-gradient-to-br from-fuchsia-500/12 via-purple-500/6 to-transparent p-8 sm:p-10">
            <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-fuchsia-500/15 blur-[70px]" />
            <Rocket className="relative text-fuchsia-300" size={30} />
            <h2 className="relative mt-6 font-outfit text-3xl font-black">
              {copy.childTitle}
            </h2>
            <p className="relative mt-4 text-base leading-8 text-white/60">
              {copy.childBody}
            </p>
          </article>
          <article className="relative overflow-hidden rounded-[2rem] border border-cyan-400/20 bg-gradient-to-br from-cyan-500/12 via-blue-500/6 to-transparent p-8 sm:p-10">
            <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-cyan-500/15 blur-[70px]" />
            <ShieldCheck className="relative text-cyan-300" size={30} />
            <h2 className="relative mt-6 font-outfit text-3xl font-black">
              {copy.parentTitle}
            </h2>
            <p className="relative mt-4 text-base leading-8 text-white/60">
              {copy.parentBody}
            </p>
          </article>
        </div>
        <div className="mx-auto mt-6 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-amber-300/15 bg-amber-400/[0.045] px-6 py-5 text-center text-sm leading-7 text-amber-50/70">
            <Star size={17} className="mx-2 inline text-amber-300" />
            {copy.allAccess}
          </div>
        </div>
      </section>

      <section className="relative px-4 pb-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2.25rem] border border-fuchsia-300/25 bg-gradient-to-br from-purple-950/90 via-[#160b2a] to-cyan-950/80 p-7 shadow-[0_25px_90px_rgba(88,28,135,0.3)] sm:p-12">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-fuchsia-300">
                {copy.urgencyEyebrow}
              </p>
              <h2 className="mt-3 font-outfit text-3xl font-black sm:text-4xl">
                {copy.urgencyTitle}
              </h2>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-white/60 sm:text-base">
                {copy.urgencyBody}
              </p>
            </div>
            {available ? (
              <a
                href={APP_LINK}
                className="group inline-flex min-h-14 items-center justify-center gap-2 rounded-full border border-white/25 bg-white px-7 text-sm font-extrabold text-[#140b25] shadow-xl transition hover:-translate-y-0.5 hover:bg-fuchsia-50"
              >
                {copy.urgencyCta}
                <ArrowRight
                  size={18}
                  className={
                    (isRtl ? "rotate-180 " : "") +
                    "transition-transform group-hover:translate-x-1"
                  }
                />
              </a>
            ) : null}
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-14 items-center justify-center rounded-full border border-white/35 px-6 text-sm font-extrabold text-white transition hover:bg-white/10"
            >
              {copy.installCta}
            </a>
          </div>
        </div>
      </section>

      <section className="relative border-t border-white/8 bg-[#07060e] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex items-center gap-3">
            <Zap className="text-cyan-300" size={23} />
            <h2 className="font-outfit text-3xl font-black">
              {copy.rulesTitle}
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {rules.map(({ icon: Icon, title, body, accent }) => (
              <article
                key={title}
                className="rounded-3xl border border-white/8 bg-white/[0.035] p-6"
              >
                <div
                  className={
                    "mb-5 flex h-11 w-11 items-center justify-center rounded-2xl border " +
                    accent
                  }
                >
                  <Icon size={19} />
                </div>
                <h3 className="font-outfit text-lg font-bold">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-white/50">{body}</p>
              </article>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-6 text-white/32">
            {copy.note}
          </p>
        </div>
      </section>
    </main>
  );
};

export default GiftPage;
